import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { womensHealthApi, patientsApi } from '../../../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Loader2, UserRound, Search, Camera, Upload,
  Stethoscope, ClipboardList, AlertTriangle, CheckCircle2, ShieldCheck
} from 'lucide-react';

const STEPS = [
  { key: 'patient', label: 'Hasta & Onam', icon: UserRound },
  { key: 'complaint', label: 'Sikayet & Tur', icon: Stethoscope },
  { key: 'images', label: 'Goruntu Yukleme', icon: Camera },
  { key: 'symptoms', label: 'Semptom Formu', icon: ClipboardList },
  { key: 'submit', label: 'Gonder', icon: CheckCircle2 },
];

export default function NewReviewPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Step 1: Patient
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [consents, setConsents] = useState({ treatment: false, intimate_imaging: false, ai_analysis: false });

  // Step 2: Complaint
  const [encounterType, setEncounterType] = useState('discharge_triage');
  const [complaint, setComplaint] = useState('');
  const [pregnant, setPregnant] = useState(false);
  const [postpartum, setPostpartum] = useState(false);

  // Step 3: Images
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [encounterId, setEncounterId] = useState(null);

  // Step 4: Symptoms
  const [symptoms, setSymptoms] = useState({
    itching: false, burning: false, pain: false, dysuria: false, dyspareunia: false,
    pelvic_pain: false, odor_present: false, odor_type: '', discharge_present: false,
    discharge_color: '', discharge_consistency: '', discharge_amount: '',
    bleeding: false, fever: false, recent_antibiotics: false, pregnant: false,
    postpartum: false, new_partner: false, sti_risk_noted: false,
    duration_days: '', last_menstrual_period: '',
  });

  // Patient search
  useEffect(() => {
    if (patientSearch.length < 2) { setPatientResults([]); return; }
    const timeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await patientsApi.list({ search: patientSearch, limit: 5 });
        setPatientResults(data.patients || data || []);
      } catch { setPatientResults([]); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [patientSearch]);

  const handleCreateEncounter = async () => {
    if (!selectedPatient) { toast.error('Hasta secin'); return; }
    if (!consents.treatment || !consents.intimate_imaging || !consents.ai_analysis) {
      toast.error('Tum onam kutularini isaretleyin'); return;
    }
    setLoading(true);
    try {
      // Create consents
      for (const [type, granted] of Object.entries(consents)) {
        if (granted) {
          await womensHealthApi.createConsent({ patient_id: selectedPatient.id, consent_type: type });
        }
      }
      // Create encounter
      const enc = await womensHealthApi.createEncounter({
        patient_id: selectedPatient.id,
        encounter_type: encounterType,
        chief_complaint: complaint,
        pregnancy_status: pregnant,
        postpartum_status: postpartum,
      });
      setEncounterId(enc.id);
      setStep(2);
      toast.success('Vaka olusturuldu');
    } catch (err) {
      toast.error('Vaka olusturulamadi: ' + (err.message || ''));
    } finally { setLoading(false); }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleUploadImages = async () => {
    if (!encounterId) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        await womensHealthApi.uploadImage(encounterId, file, encounterType === 'appearance_tracking' ? 'vulvar' : 'discharge');
        uploaded++;
      } catch (err) {
        toast.error(`${file.name} yuklenemedi`);
      }
    }
    if (uploaded > 0) toast.success(`${uploaded} goruntu yuklendi`);
    setUploading(false);
    setStep(3);
  };

  const handleSubmitSymptoms = async () => {
    if (!encounterId) return;
    setLoading(true);
    try {
      const symptomData = {
        ...symptoms,
        encounter_id: encounterId,
        duration_days: symptoms.duration_days ? parseInt(symptoms.duration_days) : null,
        pregnant, postpartum,
      };
      const result = await womensHealthApi.saveSymptoms(encounterId, symptomData);
      
      if (result.triage_flags && result.triage_flags.length > 0) {
        const urgentCount = result.triage_flags.filter(f => f.severity === 'urgent' || f.severity === 'high').length;
        if (urgentCount > 0) {
          toast.warning(`${urgentCount} acil bayrak tespit edildi!`, { duration: 5000 });
        } else {
          toast.info(`${result.triage_flags.length} triaj bayragi olusturuldu`);
        }
      }
      
      await womensHealthApi.updateEncounter(encounterId, { status: 'awaiting_review' });
      setStep(4);
      toast.success('Degerlendirme gonderildi');
    } catch (err) {
      toast.error('Gonderim hatasi: ' + (err.message || ''));
    } finally { setLoading(false); }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            {/* Patient Search */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Hasta Sec</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Hasta adi ile ara..."
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  className="pl-9"
                  data-testid="wh-patient-search"
                />
              </div>
              {searchLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              {patientResults.length > 0 && !selectedPatient && (
                <div className="border rounded-lg divide-y max-h-40 overflow-auto">
                  {patientResults.map(p => (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedPatient(p); setPatientSearch(`${p.first_name} ${p.last_name}`); setPatientResults([]); }}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 text-left text-sm"
                      data-testid={`wh-patient-result-${p.id}`}
                    >
                      <UserRound className="w-4 h-4 text-[#5A67D8]" />
                      <span>{p.first_name} {p.last_name}</span>
                      {p.pregnancy_week && <span className="text-xs text-slate-400">{p.pregnancy_week}. hafta</span>}
                    </button>
                  ))}
                </div>
              )}
              {selectedPatient && (
                <Card className="border-[#5A67D8]/20 bg-[#5A67D8]/5">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserRound className="w-4 h-4 text-[#5A67D8]" />
                      <span className="font-medium text-sm">{selectedPatient.first_name} {selectedPatient.last_name}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedPatient(null); setPatientSearch(''); }}>Degistir</Button>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Consents */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-green-600" /> Hasta Onami
              </Label>
              <div className="space-y-2 bg-green-50 p-4 rounded-lg border border-green-200">
                {[
                  { key: 'treatment', label: 'Tedavi ve muayene icin onam verildi' },
                  { key: 'intimate_imaging', label: 'Hassas bolge goruntuleme icin onam verildi' },
                  { key: 'ai_analysis', label: 'AI destekli analiz icin onam verildi' },
                ].map(c => (
                  <div key={c.key} className="flex items-center gap-2">
                    <Checkbox
                      id={`consent-${c.key}`}
                      checked={consents[c.key]}
                      onCheckedChange={(v) => setConsents(prev => ({ ...prev, [c.key]: !!v }))}
                      data-testid={`wh-consent-${c.key}`}
                    />
                    <label htmlFor={`consent-${c.key}`} className="text-sm cursor-pointer">{c.label}</label>
                  </div>
                ))}
              </div>
            </div>

            <Button
              onClick={() => setStep(1)}
              disabled={!selectedPatient || !consents.treatment || !consents.intimate_imaging || !consents.ai_analysis}
              className="w-full bg-[#5A67D8] hover:bg-[#4C51BF]"
              data-testid="wh-next-step-0"
            >
              Devam <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Degerlendirme Turu</Label>
              <Select value={encounterType} onValueChange={setEncounterType}>
                <SelectTrigger data-testid="wh-encounter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="discharge_triage">Semptom Triaji</SelectItem>
                  <SelectItem value="appearance_tracking">Gorunum Takibi</SelectItem>
                  <SelectItem value="followup">Takip Kontrolu</SelectItem>
                  <SelectItem value="general">Genel Degerlendirme</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ana Sikayet</Label>
              <Textarea
                value={complaint}
                onChange={(e) => setComplaint(e.target.value)}
                placeholder="Hastanin sikayetini yazin..."
                rows={3}
                data-testid="wh-complaint"
              />
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Checkbox id="pregnant" checked={pregnant} onCheckedChange={setPregnant} />
                <label htmlFor="pregnant" className="text-sm">Gebe</label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="postpartum" checked={postpartum} onCheckedChange={setPostpartum} />
                <label htmlFor="postpartum" className="text-sm">Lohusa</label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="w-4 h-4 mr-1" /> Geri</Button>
              <Button onClick={handleCreateEncounter} disabled={loading} className="flex-1 bg-[#5A67D8]" data-testid="wh-create-encounter">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Vaka Olustur & Devam <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
              <Camera className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500 mb-3">Tibbi goruntuleri yukleyin</p>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="wh-image-input"
                data-testid="wh-image-input"
              />
              <Button variant="outline" onClick={() => document.getElementById('wh-image-input').click()}>
                <Upload className="w-4 h-4 mr-1.5" /> Dosya Sec
              </Button>
            </div>
            {files.length > 0 && (
              <div className="space-y-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg text-sm">
                    <span className="truncate">{f.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}>Kaldir</Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(3)}>Goruntu Olmadan Atla</Button>
              <Button onClick={handleUploadImages} disabled={uploading || files.length === 0} className="flex-1 bg-[#5A67D8]" data-testid="wh-upload-images">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Yukle & Devam <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Semptom ve bulgulari isaretleyin. Bu veriler triaj kural motoru tarafindan degerlendirilecektir.</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { key: 'itching', label: 'Kasinma' },
                { key: 'burning', label: 'Yanma' },
                { key: 'pain', label: 'Agri' },
                { key: 'dysuria', label: 'Dizuri' },
                { key: 'dyspareunia', label: 'Disparoni' },
                { key: 'pelvic_pain', label: 'Pelvik Agri' },
                { key: 'bleeding', label: 'Kanama' },
                { key: 'fever', label: 'Ates' },
                { key: 'recent_antibiotics', label: 'Yakın Antibiyotik' },
                { key: 'new_partner', label: 'Yeni Partner' },
                { key: 'sti_risk_noted', label: 'STI Risk' },
              ].map(s => (
                <div key={s.key} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                  <Checkbox
                    id={`sym-${s.key}`}
                    checked={symptoms[s.key]}
                    onCheckedChange={(v) => setSymptoms(prev => ({ ...prev, [s.key]: !!v }))}
                    data-testid={`wh-sym-${s.key}`}
                  />
                  <label htmlFor={`sym-${s.key}`} className="text-sm cursor-pointer">{s.label}</label>
                </div>
              ))}
            </div>

            {/* Discharge details */}
            <Card className="border-slate-200">
              <CardHeader className="py-2 px-4"><CardTitle className="text-sm">Akinti Detaylari</CardTitle></CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="discharge_present"
                    checked={symptoms.discharge_present}
                    onCheckedChange={(v) => setSymptoms(prev => ({ ...prev, discharge_present: !!v }))}
                  />
                  <label htmlFor="discharge_present" className="text-sm">Akinti Var</label>
                </div>
                {symptoms.discharge_present && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Renk</Label>
                      <Select value={symptoms.discharge_color} onValueChange={(v) => setSymptoms(p => ({ ...p, discharge_color: v }))}>
                        <SelectTrigger><SelectValue placeholder="Sec" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="white">Beyaz</SelectItem>
                          <SelectItem value="grey">Gri</SelectItem>
                          <SelectItem value="yellow">Sari</SelectItem>
                          <SelectItem value="green">Yesil</SelectItem>
                          <SelectItem value="brown">Kahverengi</SelectItem>
                          <SelectItem value="bloody">Kanli</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Kivam</Label>
                      <Select value={symptoms.discharge_consistency} onValueChange={(v) => setSymptoms(p => ({ ...p, discharge_consistency: v }))}>
                        <SelectTrigger><SelectValue placeholder="Sec" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="thin">Ince/Sulu</SelectItem>
                          <SelectItem value="thick">Kalin</SelectItem>
                          <SelectItem value="cottage">Kesik Sut</SelectItem>
                          <SelectItem value="frothy">Kopuklu</SelectItem>
                          <SelectItem value="mucous">Mukoz</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Miktar</Label>
                      <Select value={symptoms.discharge_amount} onValueChange={(v) => setSymptoms(p => ({ ...p, discharge_amount: v }))}>
                        <SelectTrigger><SelectValue placeholder="Sec" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minimal">Az</SelectItem>
                          <SelectItem value="moderate">Orta</SelectItem>
                          <SelectItem value="heavy">Cok</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="odor_present"
                    checked={symptoms.odor_present}
                    onCheckedChange={(v) => setSymptoms(prev => ({ ...prev, odor_present: !!v }))}
                  />
                  <label htmlFor="odor_present" className="text-sm">Koku Var</label>
                </div>
                {symptoms.odor_present && (
                  <Select value={symptoms.odor_type} onValueChange={(v) => setSymptoms(p => ({ ...p, odor_type: v }))}>
                    <SelectTrigger><SelectValue placeholder="Koku turu" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fishy">Baliksi</SelectItem>
                      <SelectItem value="foul">Kotu/Pur</SelectItem>
                      <SelectItem value="musty">Kuf</SelectItem>
                      <SelectItem value="none_specific">Belirgin Degil</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Sure (gun)</Label>
                <Input
                  type="number"
                  value={symptoms.duration_days}
                  onChange={(e) => setSymptoms(p => ({ ...p, duration_days: e.target.value }))}
                  placeholder="Kac gun"
                  data-testid="wh-duration"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Son Adet Tarihi</Label>
                <Input
                  type="date"
                  value={symptoms.last_menstrual_period}
                  onChange={(e) => setSymptoms(p => ({ ...p, last_menstrual_period: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-1" /> Geri</Button>
              <Button onClick={handleSubmitSymptoms} disabled={loading} className="flex-1 bg-[#5A67D8]" data-testid="wh-submit-symptoms">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Analiz Icin Gonder <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="text-center py-12 space-y-4">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
            <h3 className="text-xl font-semibold text-slate-800">Degerlendirme Gonderildi</h3>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              Semptom verileri analiz edildi ve triaj bayraklari olusturuldu. 
              Klinisyen degerlendirme panelinden vakayi inceleyebilirsiniz.
            </p>
            <div className="flex justify-center gap-3 pt-4">
              <Button variant="outline" onClick={() => navigate('/doctor/womens-health')}>Vaka Listesi</Button>
              <Button onClick={() => navigate(`/doctor/womens-health/${encounterId}`)} className="bg-[#5A67D8]" data-testid="wh-go-to-detail">
                Vakayi Incele <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        );

      default: return null;
    }
  };

  return (
    <DashboardLayout title="Yeni Degerlendirme" subtitle="Adim adim klinik degerlendirme olusturun">
      <div className="max-w-2xl mx-auto">
        {/* Steps indicator */}
        {step < 4 && (
          <div className="flex items-center justify-between mb-6">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isDone = i < step;
              return (
                <div key={s.key} className="flex items-center">
                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                    isActive ? 'bg-[#5A67D8] text-white' : isDone ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'
                  }`}>
                    <Icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`w-6 sm:w-12 h-0.5 mx-1 ${isDone ? 'bg-green-300' : 'bg-slate-200'}`} />}
                </div>
              );
            })}
          </div>
        )}

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            {renderStep()}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
